<script context="module" lang="ts">
  export type ToggleMode = "inclusive" | "exclusive";

  export type FilterMenuItemDetails = {
    id: string;
    title: string;
    subtitle?: string;
    selected: boolean;
    unavailable?: boolean;
  };
</script>

<script lang="ts">
  import type { Action } from "svelte/action";
  import ContextMenu from "../invoices/ContextMenu.svelte";
  import type { DropdownMenuAttributes, MenuParams } from "./create";
  import FilterMenuItem from "../invoices/FilterMenuItem.svelte";
  import { push, querystring, location } from "svelte-spa-router";
  import { FILTER_MENU_MIN_WIDTH } from "../invoices/constants";
  import type { TextSize } from "../TextNew.svelte";

  export let filterQueryParam: string;
  export let menuAttrs: DropdownMenuAttributes;
  export let isOpen: boolean;
  export let toggleAllTitle: string = "toggle all";
  export let action: Action<HTMLElement, MenuParams, Record<never, any>>;
  export let menuOptions: MenuParams;
  export let items: FilterMenuItemDetails[] | undefined;
  export let toggleMode: ToggleMode = "exclusive";
  export let itemTitleSize: TextSize = "xs";
  export let onToggle: ((selected: FilterMenuItemDetails) => void) | undefined = undefined;
  export let onToggleAll: (() => void) | undefined = undefined;

  function toggleAll() {
    if (onToggleAll) {
      onToggleAll();
      return;
    }
    const queryParams = new URLSearchParams($querystring);
    if (!queryParams.has(filterQueryParam)) {
      return;
    }
    queryParams.delete(filterQueryParam);
    pushQueryRoute(queryParams);
  }

  function pushQueryRoute(queryParams: URLSearchParams) {
    if (queryParams.size > 0) {
      push(`${$location}?${queryParams.toString()}`);
    } else {
      push($location);
    }
  }

  function toggleQueryParamID(queryParams: URLSearchParams, id: string) {
    // svelte seems to have an issue with the optional 2nd parameter in URLSearchParams methods.
    // Not too sure why, but ts-ignoring them for now.
    // @ts-ignore
    if (queryParams.has(filterQueryParam, id)) {
      // @ts-ignore
      queryParams.delete(filterQueryParam, id);
    } else {
      // @ts-ignore
      queryParams.append(filterQueryParam, id);
    }
  }

  function isLastItemToBeSelected(
    items: FilterMenuItemDetails[],
    toggledItem: FilterMenuItemDetails
  ) {
    const unselectedChatItem = items.filter((c) => !c.selected);
    return unselectedChatItem.length === 1 && unselectedChatItem[0].id === toggledItem.id;
  }

  function handleExclusiveToggleClick(toggledItem: FilterMenuItemDetails) {
    if (!items) {
      return;
    }
    if (onToggle) {
      onToggle(toggledItem);
      return;
    }
    const queryParams = new URLSearchParams($querystring);
    if (!queryParams.has(filterQueryParam)) {
      for (const c of items) {
        if (toggledItem.id !== c.id) {
          queryParams.append(filterQueryParam, c.id);
        }
      }
      pushQueryRoute(queryParams);
      return;
    }

    if (isLastItemToBeSelected(items, toggledItem)) {
      queryParams.delete(filterQueryParam);
      pushQueryRoute(queryParams);
      return;
    }

    toggleQueryParamID(queryParams, toggledItem.id);
    pushQueryRoute(queryParams);
  }
</script>

{#if isOpen}
  <ContextMenu {...menuAttrs} {action} {menuOptions} --filter-menu-minW={FILTER_MENU_MIN_WIDTH}>
    <FilterMenuItem on:click={toggleAll} title={toggleAllTitle} titleSize={itemTitleSize} />
    {#if items}
      {#each items as item (item.id)}
        <FilterMenuItem
          title={item.title}
          subtitle={item.subtitle}
          selected={item.selected}
          unavailable={item.unavailable}
          titleSize={itemTitleSize}
          on:click={() => {
            if (!items) {
              return;
            }

            if (toggleMode === "exclusive") {
              handleExclusiveToggleClick(item);
              return;
            }

            const queryParams = new URLSearchParams($querystring);

            if (isLastItemToBeSelected(items, item) || (items.length === 1 && item.selected)) {
              queryParams.delete(filterQueryParam);
              pushQueryRoute(queryParams);
              return;
            }

            toggleQueryParamID(queryParams, item.id);
            pushQueryRoute(queryParams);
          }}
        />
      {/each}
    {/if}
  </ContextMenu>
{/if}
